import { useState } from 'react';
import { Rocket, Type, FileCode, User } from 'lucide-react';
import './SpawnForm.css';

const AGENTS = [
  { id: 'codex', name: 'Codex', emoji: '🔧', desc: 'Best for backend, APIs, multi-file refactors' },
  { id: 'claude-sonnet', name: 'Claude Sonnet', emoji: '⚡', desc: 'Fast, great for UI/frontend work' },
  { id: 'claude-opus', name: 'Claude Opus', emoji: '🧠', desc: 'Deep reasoning, architecture, complex logic' },
  { id: 'kimi', name: 'Kimi K2.5', emoji: '👁️', desc: 'Code review, analysis, security checks' },
];

const TYPES = [
  { id: 'feature', name: 'Feature', color: '#3b82f6' },
  { id: 'bug', name: 'Bug Fix', color: '#ef4444' },
  { id: 'refactor', name: 'Refactor', color: '#8b5cf6' },
  { id: 'review', name: 'Code Review', color: '#f59e0b' },
];

function SpawnForm({ onSpawn }) {
  const [description, setDescription] = useState('');
  const [type, setType] = useState('feature');
  const [agent, setAgent] = useState('');
  const [context, setContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) return;

    setLoading(true);
    
    const ctx = {};
    if (context.trim()) {
      ctx.notes = context.trim();
    }
    if (agent) {
      ctx.forceAgent = agent;
    }

    await onSpawn(type, description, ctx);
    
    setLoading(false);
    setSuccess(true);
    setDescription('');
    setContext('');
    
    setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <div className="spawn-form">
      <h2>Spawn New Task</h2>
      
      {success && (
        <div className="success-message">
          ✓ Task spawned successfully!
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>
            <Type size={16} />
            Task Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what needs to be built..."
            rows={3}
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Task Type</label>
            <div className="type-selector">
              {TYPES.map(t => (
                <button
                  key={t.id}
                  type="button"
                  className={type === t.id ? 'active' : ''}
                  onClick={() => setType(t.id)}
                  style={{ 
                    '--type-color': t.color,
                    borderColor: type === t.id ? t.color : undefined 
                  }}
                >
                  {t.name}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Agent (Optional)</label>
            <select 
              value={agent} 
              onChange={(e) => setAgent(e.target.value)}
            >
              <option value="">Auto-select</option>
              {AGENTS.map(a => (
                <option key={a.id} value={a.id}>
                  {a.emoji} {a.name}
                </option>
              ))}
            </select>            
            {agent && (
              <p className="agent-desc">
                {AGENTS.find(a => a.id === agent)?.desc}
              </p>
            )}
          </div>
        </div>

        <div className="form-group">
          <label>
            <User size={16} />
            Context (Optional)
          </label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Additional context: customer info, meeting notes, file paths..."
            rows={2}
          />
        </div>

        <button 
          type="submit" 
          className="btn-spawn"
          disabled={loading || !description.trim()}
        >
          {loading ? (
            <>Loading...</>
          ) : (
            <>
              <Rocket size={18} />
              Spawn Agent
            </>
          )}
        </button>
      </form>

      <div className="tips">
        <h4>💡 Tips</h4>
        <ul>
          <li>Be specific about what you want built</li>
          <li>Mention file paths if you know them</li>
          <li>Let Zoe auto-select the agent unless you have a preference</li>
          <li>Include customer context for business-critical tasks</li>
        </ul>
      </div>
    </div>
  );
}

export default SpawnForm;
